import { notificationEvents } from './notificationEvents';
import { BASE_URL } from '../config';

// Always connect directly to the backend, bypassing the CRA dev proxy.
// The proxy doesn't sustain long-lived SSE connections properly.
const getSSEBaseUrl = () => BASE_URL;

class SSEService {
	constructor() {
		this.eventSource = null;
		this.reconnectAttempts = 0;
		this.maxReconnectAttempts = 3; // Reduced from 10 to 3
		this.reconnectDelay = 2000; // Start with 2 seconds
		this.maxReconnectDelay = 60000; // Max 60 seconds between reconnection attempts
		this.isConnected = false;
		this.isConnecting = false; // Track connection state to prevent race conditions
		this.token = null;
		this.listeners = new Map();
		this.reconnectTimeout = null;
		this.heartbeatTimeout = null;
		this.lastHeartbeat = null;
		this.heartbeatInterval = 30000; // Expect heartbeat every 30 seconds
		this.circuitBreakerOpen = false;
		this.consecutiveFailures = 0;
		this.lastConnectionAttempt = null;
	}

	/**
	 * Connect to the SSE endpoint
	 * @param {string} token - JWT token for authentication
	 */
	async connect(token) {
		// SINGLETON PROTECTION: Prevent multiple concurrent connections
		if (
			this.eventSource &&
			this.eventSource.readyState !== EventSource.CLOSED
		) {
			if (process.env.NODE_ENV === 'development') {
				console.log('📡 SSE: Already connected or connecting');
			}
			return;
		}

		// Additional check: if we're already in the middle of connecting, don't start another
		if (this.isConnecting) {
			if (process.env.NODE_ENV === 'development') {
				console.log('📡 SSE: Connection already in progress, skipping');
			}
			return;
		}

		// Mark as connecting to prevent race conditions
		this.isConnecting = true;

		// Circuit breaker check
		if (this.circuitBreakerOpen) {
			const now = Date.now();
			if (this.lastConnectionAttempt && (now - this.lastConnectionAttempt) < 5 * 60 * 1000) {
				if (process.env.NODE_ENV === 'development') {
					console.log('🚫 SSE: Circuit breaker OPEN, skipping connection attempt');
				}
				this.isConnecting = false; // Reset connecting flag
				return;
			} else {
				// Reset circuit breaker after timeout
				console.log('🔄 SSE: Circuit breaker timeout passed, attempting to reconnect');
				this.circuitBreakerOpen = false;
				this.consecutiveFailures = 0;
			}
		}

		// Close any existing connection before creating new one
		if (this.eventSource) {
			if (process.env.NODE_ENV === 'development') {
				console.log('📡 SSE: Closing existing connection before creating new one');
			}
			this.eventSource.close();
			this.eventSource = null;
		}

		this.token = token;
		this.lastConnectionAttempt = Date.now();
		if (process.env.NODE_ENV === 'development') {
			console.log('📡 SSE: Connecting to notification stream...');
		}

		try {
			// Try to get a secure stream token from the server (preferred method)
			const streamToken = await this.getSecureStreamToken(token);

			let url;

			if (streamToken) {
				// Use secure stream token (preferred)
				url = `${getSSEBaseUrl()}/api/admin/forms/notifications/stream?stream_token=${encodeURIComponent(streamToken)}`;
					if (process.env.NODE_ENV === 'development') {
					console.log('📡 SSE: Using secure stream token (direct connection)');
				}
			} else {
				// Fallback to direct token (less secure but functional)
				url = `${getSSEBaseUrl()}/api/admin/forms/notifications/stream?token=${encodeURIComponent(token)}`;
					if (process.env.NODE_ENV === 'development') {
					console.warn('📡 SSE: Falling back to direct token authentication (direct connection)');
				}
			}

			this.eventSource = new EventSource(url);

			// Capture reference so handlers only act on THIS connection,
			// not a newer one that may have replaced it.
			const es = this.eventSource;

			// Handle connection open
			es.onopen = (event) => {
				if (this.eventSource !== es) return; // Stale connection
				if (process.env.NODE_ENV === 'development') {
					console.log('📡 SSE: Connection established');
				}
				this.isConnected = true;
				this.isConnecting = false;
				this.reconnectAttempts = 0;
				this.reconnectDelay = 2000;
				this.consecutiveFailures = 0;
				this.circuitBreakerOpen = false;
				this.lastHeartbeat = Date.now();
				this.resetHeartbeatTimeout();
				this.emit('connected', { timestamp: new Date().toISOString() });
			};

			// Handle incoming messages
			es.onmessage = (event) => {
				if (this.eventSource !== es) return; // Stale connection
				try {
					const data = JSON.parse(event.data);
						if (process.env.NODE_ENV === 'development') {
						console.log('📡 SSE: Received message:', data);
					}
					this.handleMessage(data);
				} catch (error) {
					console.error('📡 SSE: Error parsing message:', error);
				}
			};

			// Handle connection errors
			es.onerror = (event) => {
				// If this EventSource has been replaced by a newer connection,
				// ignore the error — it's from a stale connection being closed.
				if (this.eventSource !== es) {
					es.close();
					return;
				}
				if (process.env.NODE_ENV === 'development') {
					console.error('📡 SSE: Connection error:', event);
				}
				this.isConnected = false;
				this.isConnecting = false;

				// Close the failed EventSource
				es.close();
				this.eventSource = null;

				this.emit('error', {
					error: event,
					timestamp: new Date().toISOString(),
				});

				// Attempt to reconnect
				this.handleReconnect();
			};
		} catch (error) {
			console.error('📡 SSE: Failed to create connection:', error);
			this.isConnecting = false; // Reset connecting flag on exception
			this.consecutiveFailures++;
			this.handleReconnect();
		}
	}

	/**
	 * Get a secure stream token from the server
	 * @param {string} token - JWT token for authentication
	 * @returns {string|null} - Secure stream token or null if failed
	 */
	async getSecureStreamToken(token) {
		try {
			const response = await fetch(`${getSSEBaseUrl()}/api/admin/forms/notifications/stream-token`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			if (response.ok) {
				const data = await response.json();
				return data.streamToken;
			} else {
				// If endpoint doesn't exist (404) or returns error, fall back to direct token
					if (process.env.NODE_ENV === 'development') {
					console.warn('📡 SSE: Stream token endpoint not available, falling back to direct token');
				}
				return null;
			}
		} catch (error) {
			// Network error or endpoint doesn't exist, fall back to direct token
				if (process.env.NODE_ENV === 'development') {
					console.warn('📡 SSE: Stream token request failed, falling back to direct token:', error.message);
				}
			return null;
		}
	}

	/**
	 * Handle incoming SSE messages
	 * @param {object} data - Message data
	 */
	handleMessage(data) {
		switch (data.type) {
			case 'connected':
					if (process.env.NODE_ENV === 'development') {
					console.log('📡 SSE: Server confirmed connection');
				}
				break;

			case 'heartbeat':
					if (process.env.NODE_ENV === 'development') {
					console.log('📡 SSE: Heartbeat received');
				}
				this.lastHeartbeat = Date.now();
				this.resetHeartbeatTimeout();
				break;

			case 'count_update':
					if (process.env.NODE_ENV === 'development') {
					console.log(`📡 SSE: Count update - ${data.count} notifications`);
				}
				// Trigger notification count update in components
				notificationEvents.refreshCount();
				this.emit('count_update', data);
				break;

			case 'new_notification':
					if (process.env.NODE_ENV === 'development') {
					console.log('📡 SSE: New notification received:', data.notification);
				}
				// Don't call refreshCount() here - the NotificationContext handles immediate increment
				// Calling refreshCount() causes a database fetch that creates delay
				this.emit('new_notification', data);
				break;

			case 'notification_seen':
					if (process.env.NODE_ENV === 'development') {
					console.log(
						`📡 SSE: Notification ${data.notificationId} marked as seen by ${data.adminUsername}`
					);
				}
				// Trigger notification refresh in components
				this.emit('notification_seen', data);
				break;

			case 'all_notifications_seen':
					if (process.env.NODE_ENV === 'development') {
					console.log(
						`📡 SSE: All notifications marked as seen by ${data.adminUsername}`
					);
				}
				// Trigger notification refresh in components
				this.emit('all_notifications_seen', data);
				break;

			default:
					if (process.env.NODE_ENV === 'development') {
					console.log('📡 SSE: Unknown message type:', data.type);
				}
		}
	}

	/**
	 * Handle reconnection logic with circuit breaker protection
	 */
	handleReconnect() {
		// Clear any existing reconnect timeout
		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		this.consecutiveFailures++;

		// Open circuit breaker after repeated failures
		if (this.consecutiveFailures >= 5) {
			console.warn(`🚨 SSE: Circuit breaker OPEN after ${this.consecutiveFailures} consecutive failures`);
			this.circuitBreakerOpen = true;
			
			// When circuit breaker opens, close any existing connections
			if (this.eventSource) {
				this.eventSource.close();
				this.eventSource = null;
			}
			this.isConnected = false;
			this.isConnecting = false;

			this.emit('circuit_breaker_open', { failures: this.consecutiveFailures });
			return;
		}

		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.error(`📡 SSE: Max reconnection attempts (${this.maxReconnectAttempts}) reached. Opening circuit breaker.`);
			this.circuitBreakerOpen = true;

			// When circuit breaker opens, close any existing connections  
			if (this.eventSource) {
				this.eventSource.close();
				this.eventSource = null;
			}
			this.isConnected = false;
			this.isConnecting = false;
			
			this.emit('max_reconnect_attempts', { attempts: this.reconnectAttempts });
			return;
		}

		this.reconnectAttempts++;
		// Improved exponential backoff with jitter and max cap
		const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
		const jitter = Math.random() * 2000; // Add randomness to prevent thundering herd
		const delay = Math.min(baseDelay + jitter, this.maxReconnectDelay);

		console.log(
			`📡 SSE: Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
		);

		this.reconnectTimeout = setTimeout(async () => {
			if (this.token && !this.circuitBreakerOpen) {
				try {
					await this.connect(this.token);
				} catch (error) {
					console.error('📡 SSE: Reconnection failed:', error);
					this.emit('reconnection_failed', { 
						error: error.message, 
						attempt: this.reconnectAttempts 
					});
				}
			}
		}, delay);
	}

	/**
	 * Reset heartbeat timeout monitoring
	 */
	resetHeartbeatTimeout() {
		if (this.heartbeatTimeout) {
			clearTimeout(this.heartbeatTimeout);
		}

		this.heartbeatTimeout = setTimeout(() => {
			console.warn('📡 SSE: Heartbeat timeout - connection may be stale');
			this.handleReconnect();
		}, this.heartbeatInterval + 10000); // 10 second grace period
	}

	/**
	 * Disconnect from SSE
	 */
	disconnect() {
		console.log('📡 SSE: Disconnecting...');

		if (this.eventSource) {
			this.eventSource.close();
			this.eventSource = null;
		}

		// Clean up timeouts
		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		if (this.heartbeatTimeout) {
			clearTimeout(this.heartbeatTimeout);
			this.heartbeatTimeout = null;
		}

		this.isConnected = false;
		this.isConnecting = false; // Reset connecting flag on disconnect
		this.reconnectAttempts = 0;
		this.token = null;
		this.lastHeartbeat = null;
		this.emit('disconnected', { timestamp: new Date().toISOString() });
	}

	/**
	 * Add event listener
	 * @param {string} event - Event name
	 * @param {function} callback - Callback function
	 */
	addEventListener(event, callback) {
		if (!this.listeners.has(event)) {
			this.listeners.set(event, []);
		}
		const callbacks = this.listeners.get(event);
		// Prevent duplicate listeners (handles React StrictMode double-invocation)
		if (!callbacks.includes(callback)) {
			callbacks.push(callback);
		}
	}

	/**
	 * Remove event listener
	 * @param {string} event - Event name
	 * @param {function} callback - Callback function
	 */
	removeEventListener(event, callback) {
		if (this.listeners.has(event)) {
			const callbacks = this.listeners.get(event);
			const index = callbacks.indexOf(callback);
			if (index > -1) {
				callbacks.splice(index, 1);
			}
		}
	}

	/**
	 * Emit event to listeners
	 * @param {string} event - Event name
	 * @param {object} data - Event data
	 */
	emit(event, data) {
		if (this.listeners.has(event)) {
			this.listeners.get(event).forEach((callback) => {
				try {
					callback(data);
				} catch (error) {
					console.error(`📡 SSE: Error in event listener for ${event}:`, error);
				}
			});
		}
	}

	/**
	 * Get connection status
	 * @returns {boolean} Connection status
	 */
	isConnectedStatus() {
		return (
			this.isConnected &&
			this.eventSource &&
			this.eventSource.readyState === EventSource.OPEN
		);
	}

	/**
	 * Get ready state
	 * @returns {number} EventSource ready state
	 */
	getReadyState() {
		return this.eventSource ? this.eventSource.readyState : EventSource.CLOSED;
	}
}

// Create singleton instance
const sseService = new SSEService();

export default sseService;
